import { FC, useEffect, useMemo, useState } from 'react'
import { crmApi } from '@/api'
import {
  LeadHistory,
  LeadHistoryChangedByItem,
} from '@/api/types/crm/crm.types'
import type { LeadProfileLead } from '../../LeadProfileModal.types'
import Preloader from '@UI/Preloader'
import cn from 'classnames'
import classes from './index.module.css'
import HistoryItem from './HistoryItem'
import usePagination from '@/hooks/usePagination'
import Pagination from '@UI/New/Pagination'
import Button from '@UI/New/Button'
import {
  ReloadOutlined,
  LoadingOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import { Empty } from 'antd'
import Select from '@pages/Admin/CRM/components/base/Select/Select'
import { useAddAlert } from '@/hooks/useAddAlert'
import LeadProfileModalBlock from '../../components/LeadProfileModalBlock/LeadProfileModalBlock'
import { useLeadRefetch } from '../../LeadRefetchContext'
import { getLeadFunnelRowId } from '../../LeadRefetchContext/leadRefetch.utils'

interface HistoryListBlockProps {
  lead: LeadProfileLead
  refreshKey?: number
}

const HistoryListBlock: FC<HistoryListBlockProps> = ({ lead, refreshKey }) => {
  const addAlert = useAddAlert()
  const { leadCardSnapshot, multipleDealsEnabled } = useLeadRefetch()

  const [selectedChangedByUserId, setSelectedChangedByUserId] = useState<
    number | null
  >(null)
  const [selectedLeadFunnelIdFilter, setSelectedLeadFunnelIdFilter] = useState<
    number | null
  >(null)
  const [selectedFunnelIdFilter, setSelectedFunnelIdFilter] = useState<
    number | null
  >(null)
  const [changedByUsers, setChangedByUsers] = useState<
    LeadHistoryChangedByItem[]
  >([])
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false)

  const currentFunnels = useMemo(() => {
    const list = leadCardSnapshot?.current_funnels
    return Array.isArray(list) ? list : []
  }, [leadCardSnapshot])

  const dealFilterOptions = useMemo(
    () =>
      currentFunnels.map((row) => {
        const leadFunnelId = getLeadFunnelRowId(row)
        const dealName = row.deal?.name?.trim()
        const funnelName = row.funnel_name?.trim()
        const label = dealName
          ? funnelName
            ? `${dealName} · ${funnelName}`
            : dealName
          : funnelName
            ? `Сделка #${leadFunnelId} · ${funnelName}`
            : `Сделка #${leadFunnelId}`
        return { value: leadFunnelId, label }
      }),
    [currentFunnels],
  )

  const dealLabelsByLeadFunnelId = useMemo(() => {
    const map: Record<number, string> = {}
    for (const row of currentFunnels) {
      const leadFunnelId = getLeadFunnelRowId(row)
      const dealName = row.deal?.name?.trim()
      map[leadFunnelId] = dealName || `Сделка #${leadFunnelId}`
    }
    return map
  }, [currentFunnels])

  const funnelFilterOptions = useMemo(() => {
    const byId = new Map<number, string>()

    for (const row of currentFunnels) {
      const funnelId = Number(row.funnel_id)
      if (!Number.isFinite(funnelId) || funnelId <= 0) continue
      if (!byId.has(funnelId)) {
        byId.set(funnelId, row.funnel_name?.trim() || `Воронка #${funnelId}`)
      }
    }

    if (lead.funnels?.length) {
      for (const funnel of lead.funnels) {
        const funnelId = Number(funnel.id)
        if (!Number.isFinite(funnelId) || funnelId <= 0) continue
        if (!byId.has(funnelId)) {
          byId.set(funnelId, funnel.name?.trim() || `Воронка #${funnelId}`)
        }
      }
    }

    return Array.from(byId.entries()).map(([value, label]) => ({
      value,
      label,
    }))
  }, [currentFunnels, lead.funnels])

  const changedByUserOptions = useMemo(
    () =>
      changedByUsers.map((user) => ({
        value: user.user_id,
        label: user.name || `ID ${user.user_id}`,
      })),
    [changedByUsers],
  )

  const {
    state: dirtyData,
    loading,
    getState: refetchQuery,
    setCurrentPage: setPaginationCurrentPage,
    ...paginationParams
  } = usePagination({
    api: (params: {
      lead_id: number
      paramsPagination: { perPage: number; page: number }
    }) => {
      return crmApi
        .getLeadHistory({
          ...params,
          changed_by: selectedChangedByUserId ?? undefined,
          lead_funnel_id: selectedLeadFunnelIdFilter ?? undefined,
          funnel_id: selectedFunnelIdFilter ?? undefined,
        })
        .then((res) => {
          const responseData = res?.data as any
          const changedBy =
            responseData?.changed_by ??
            (
              responseData?.data as
                | { changed_by?: LeadHistoryChangedByItem[] }
                | undefined
            )?.changed_by
          if (Array.isArray(changedBy)) {
            setChangedByUsers(changedBy)
          }
          return res
        })
    },
    params: {
      lead_id: lead.id,
    },
  })

  const data = (dirtyData ?? []) as LeadHistory[]

  useEffect(() => {
    if (refreshKey === undefined) return
    refetchQuery()
  }, [refreshKey])

  useEffect(() => {
    setPaginationCurrentPage(1)
    refetchQuery()
  }, [
    selectedChangedByUserId,
    selectedLeadFunnelIdFilter,
    selectedFunnelIdFilter,
  ])

  const handleDownloadExcel = async () => {
    try {
      setIsDownloadingExcel(true)
      const res = await crmApi.downloadLeadHistoryExcel({ lead_id: lead.id })
      const contentDisposition =
        (res.headers?.['content-disposition'] as string | undefined) ?? ''
      const filenameMatch = contentDisposition.match(
        /filename\*?=(?:UTF-8''|")?([^\";]+)/i,
      )
      const decodedFilename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim())
        : null
      const fileName = decodedFilename || `lead-history-${lead.id}.xlsx`
      const blob = new Blob([res.data], {
        type: 'application/octet-stream',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось выгрузить историю в Excel',
        type: 'error',
      })
    } finally {
      setIsDownloadingExcel(false)
    }
  }

  const showDealFilter = multipleDealsEnabled && dealFilterOptions.length > 0
  const showFunnelFilter = funnelFilterOptions.length > 0

  return (
    <LeadProfileModalBlock
      className={classes.historySectionStretch}
      innerClassName={classes.historyInnerStretch}
      title="История"
    >
      <div className={classes.toolbar}>
        <div className={classes.filtersRow}>
          {showDealFilter ? (
            <Select
              size="small"
              allowClear
              showSearch
              placeholder="Все сделки"
              className={classes.filterSelect}
              options={dealFilterOptions}
              value={selectedLeadFunnelIdFilter ?? undefined}
              onChange={(value) =>
                setSelectedLeadFunnelIdFilter(
                  value === undefined || value === null ? null : Number(value),
                )
              }
            />
          ) : null}

          {showFunnelFilter ? (
            <Select
              size="small"
              allowClear
              showSearch
              placeholder="Все воронки"
              className={classes.filterSelect}
              options={funnelFilterOptions}
              value={selectedFunnelIdFilter ?? undefined}
              onChange={(value) =>
                setSelectedFunnelIdFilter(
                  value === undefined || value === null ? null : Number(value),
                )
              }
            />
          ) : null}

          <Select
            size="small"
            allowClear
            showSearch
            placeholder="Кто вносил изменения"
            className={classes.filterSelect}
            options={changedByUserOptions}
            value={selectedChangedByUserId ?? undefined}
            onChange={(value) =>
              setSelectedChangedByUserId(
                value === undefined || value === null ? null : Number(value),
              )
            }
          />
        </div>

        <div className={cn(classes.buttonsContainer)}>
          <Button
            variant="action"
            color="gray"
            className={classes.refreshBtn}
            onClick={() => refetchQuery()}
            disabled={loading}
            leftContent={
              loading ? (
                <LoadingOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              ) : (
                <ReloadOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              )
            }
          >
            Обновить
          </Button>
          <Button
            variant="action"
            color="gray"
            className={classes.downloadButton}
            onClick={handleDownloadExcel}
            isLoading={isDownloadingExcel}
            leftContent={
              !isDownloadingExcel ? (
                <DownloadOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              ) : undefined
            }
          >
            Скачать Excel
          </Button>
        </div>
      </div>

      <div className={cn('custom-scroll', classes.timelineScroll)}>
        {loading ? (
          <div className={classes.preloaderWrapper}>
            <Preloader />
          </div>
        ) : data.length < 1 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Ничего не найдено"
            className={classes.emptyState}
          />
        ) : (
          data.map((item) => (
            <HistoryItem
              key={item.id}
              historyItem={item}
              leadFields={'fields' in lead ? lead.fields : undefined}
              dealLabelsByLeadFunnelId={
                multipleDealsEnabled ? dealLabelsByLeadFunnelId : undefined
              }
              showDealScope={multipleDealsEnabled}
            />
          ))
        )}
      </div>

      <div className={classes.footerBar}>
        <div className={classes.paginationGrow}>
          <Pagination
            {...paginationParams}
            setCurrentPage={setPaginationCurrentPage}
          />
        </div>
      </div>
    </LeadProfileModalBlock>
  )
}

export default HistoryListBlock
